import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

function getLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

const METRICS = [
  { key: 'steps',    label: 'Steps',           unit: 'steps', color: '#4caf7d', icon: '👟', goal: 10000 },
  { key: 'sleep',    label: 'Sleep',           unit: 'hrs',   color: '#9b5ce0', icon: '😴', goal: 8 },
  { key: 'calories', label: 'Calories Burned', unit: 'kcal',  color: '#e07d3c', icon: '🔥', goal: 500 },
  { key: 'heart',    label: 'Resting HR',      unit: 'bpm',   color: '#e05c5c', icon: '❤️', goal: 70 },
  { key: 'active',   label: 'Active Minutes',  unit: 'min',   color: '#5c8fe0', icon: '⚡', goal: 30 },
]

function calculateHealthScore(data) {
  let score = 0
  if (data.steps)    score += Math.min(30, (data.steps / 10000) * 30)
  if (data.sleep)    score += Math.min(20, (data.sleep / 8) * 20)
  if (data.calories) score += Math.min(20, (data.calories / 500) * 20)
  if (data.active)   score += Math.min(20, (data.active / 30) * 20)
  if (data.heart)    score += data.heart < 80 ? 10 : data.heart < 90 ? 5 : 0
  return Math.min(100, Math.round(score))
}

export default function HealthPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const [linked, setLinked] = useState(false)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [healthData, setHealthData] = useState({})
  const [lastSync, setLastSync] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    checkIfLinked()
  }, [])

  async function checkIfLinked() {
    try {
      const { Health } = await import(/* webpackIgnore: true */ '@capacitor-community/health')
      const { available } = await Health.isAvailable()
      if (available) {
        // Try to read — if it works we're already linked
        const result = await Health.requestAuthorization({
          read: ['steps', 'calories', 'heart_rate', 'sleep', 'activity'],
          write: []
        })
        if (result.authorized) {
          setLinked(true)
          loadHealthData()
        }
      }
    } catch (e) {
      // Not on iOS or plugin not available — stay unlinked
    }
  }

  async function linkAppleHealth() {
    setLoading(true)
    setError('')
    try {
      const { Health } = await import(/* webpackIgnore: true */ '@capacitor-community/health')
      const { available } = await Health.isAvailable()
      if (!available) {
        setError('Apple Health is only available on iPhone.')
        setLoading(false)
        return
      }
      const result = await Health.requestAuthorization({
        read: ['steps', 'calories', 'heart_rate', 'sleep', 'activity'],
        write: []
      })
      if (result.authorized) {
        setLinked(true)
        await loadHealthData()
      } else {
        setError('Permission denied. Please allow Health access in iPhone Settings.')
      }
    } catch (e) {
      setError('Could not connect to Apple Health. Make sure you\'re on an iPhone.')
    }
    setLoading(false)
  }

  async function loadHealthData() {
    setSyncing(true)
    try {
      const { Health } = await import(/* webpackIgnore: true */ '@capacitor-community/health')
      const now = new Date()
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())

      const data = {}

      // Steps
      try {
        const steps = await Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'steps',
          bucket: 'day'
        })
        data.steps = Math.round(steps?.data?.[0]?.value || 0)
      } catch {}

      // Calories
      try {
        const cal = await Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'calories',
          bucket: 'day'
        })
        data.calories = Math.round(cal?.data?.[0]?.value || 0)
      } catch {}

      // Active minutes
      try {
        const active = await Health.queryAggregated({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'activity',
          bucket: 'day'
        })
        data.active = Math.round(active?.data?.[0]?.value || 0)
      } catch {}

      // Sleep (last night)
      try {
        const sleepStart = new Date(startOfDay.getTime() - 12 * 60 * 60 * 1000)
        const sleep = await Health.queryAggregated({
          startDate: sleepStart.toISOString(),
          endDate: startOfDay.toISOString(),
          dataType: 'sleep',
          bucket: 'day'
        })
        data.sleep = Math.round((sleep?.data?.[0]?.value || 0) / 60 * 10) / 10
      } catch {}

      // Heart rate
      try {
        const heart = await Health.query({
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          dataType: 'heart_rate',
          limit: 10
        })
        if (heart?.data?.length > 0) {
          const vals = heart.data.map(d => d.value)
          data.heart = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
        }
      } catch {}

      setHealthData(data)
      setLastSync(new Date())

      // Save health score to manual_tasks
      const healthScore = calculateHealthScore(data)
      if (healthScore > 0 && user) {
        await supabase.from('manual_tasks').upsert({
          user_id: user.id,
          date: getLocalDate(),
          description: 'Apple Health data',
          category: 'Health Sync',
          area: 'health',
          base_points: healthScore,
          time_minutes: 0,
          final_points: healthScore,
          flagged: false,
          flag_reason: '',
        }, { onConflict: 'user_id,date,description' })
      }

    } catch (e) {
      setError('Could not load Health data.')
    }
    setSyncing(false)
  }

  const healthScore = calculateHealthScore(healthData)

  if (!linked) {
    return (
      <div style={s.page}>
        <div style={s.ambient} />
        <div style={s.header}>
          <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
          <div style={s.headerTitle}>Health</div>
          <div style={{ width: 36 }} />
        </div>
        <div style={s.linkWrap}>
          <div style={s.linkCard}>
            <div style={s.linkIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402C1 3.543 4.068 2 6.281 2c1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447C20.266 2.01 23 3.631 23 7.191c0 4.069-5.136 8.625-11 14.402z" stroke="#e05c5c" strokeWidth="1.8" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={s.linkTitle}>Link Apple Health</div>
            <div style={s.linkDesc}>Connect Apple Health to automatically track your steps, sleep, calories, heart rate, and active minutes — and earn health points automatically.</div>
            {error ? <div style={s.errorText}>{error}</div> : null}
            <div style={{ ...s.linkBtn, opacity: loading ? 0.6 : 1 }} onClick={linkAppleHealth}>
              {loading ? 'Connecting...' : 'Connect Apple Health →'}
            </div>
            <div style={s.linkSkip} onClick={() => onNavigate('dashboard')}>Not now</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={s.page}>
      <div style={s.ambient} />
      <div style={s.header}>
        <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={s.headerTitle}>Health</div>
        <div style={s.linkedBadge}>Linked ✓</div>
      </div>

      <div style={s.main}>
        {/* HEALTH SCORE */}
        <div style={s.scoreCard}>
          <div style={s.scoreLabel}>Today's Health Score</div>
          <div style={{ ...s.scoreVal, color: healthScore >= 75 ? '#4caf7d' : healthScore >= 50 ? '#c9a84c' : '#e05c5c' }}>
            {healthScore}
          </div>
          <div style={s.scoreSub}>out of 100</div>
          <div style={{ ...s.syncBtn, opacity: syncing ? 0.6 : 1 }} onClick={loadHealthData}>
            {syncing ? 'Syncing...' : '↻ Sync Now'}
          </div>
          {lastSync && (
            <div style={s.lastSync}>Last synced {lastSync.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
          )}
        </div>

        {/* METRICS GRID */}
        <div style={s.metricsGrid}>
          {METRICS.map(m => {
            const val = healthData[m.key]
            const pct = val ? Math.min(100, (val / m.goal) * 100) : 0
            return (
              <div key={m.key} style={s.metricCard}>
                <div style={s.metricTop}>
                  <span style={s.metricIcon}>{m.icon}</span>
                  <div style={{ ...s.metricVal, color: m.color }}>
                    {val !== undefined ? val.toLocaleString() : '—'}
                    <span style={s.metricUnit}> {m.unit}</span>
                  </div>
                </div>
                <div style={s.metricLabel}>{m.label}</div>
                <div style={s.metricBarBg}>
                  <div style={{ ...s.metricBarFill, width: `${pct}%`, background: m.color }} />
                </div>
                <div style={s.metricGoal}>Goal: {m.goal.toLocaleString()} {m.unit}</div>
              </div>
            )
          })}
        </div>

        <div style={s.noteCard}>
          <div style={s.noteText}>Health data syncs automatically when you open this page. Points are awarded to your Health life area based on your daily activity.</div>
        </div>
      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'none', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(13,13,20,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#e8e6df' },
  linkedBadge: { fontSize: '11px', fontWeight: '600', color: '#4caf7d', background: 'rgba(76,175,125,0.12)', border: '1px solid rgba(76,175,125,0.2)', padding: '4px 10px', borderRadius: '8px' },
  linkWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', position: 'relative', zIndex: 1 },
  linkCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '36px 28px', textAlign: 'center', maxWidth: '340px', width: '100%' },
  linkIcon: { display: 'flex', justifyContent: 'center', marginBottom: '20px' },
  linkTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '3px', color: '#e8e6df', marginBottom: '12px' },
  linkDesc: { fontSize: '14px', color: '#6b6860', lineHeight: '1.6', marginBottom: '24px' },
  errorText: { fontSize: '13px', color: '#e05c5c', marginBottom: '12px', padding: '10px', background: 'rgba(224,92,92,0.08)', borderRadius: '8px' },
  linkBtn: { padding: '14px', background: 'linear-gradient(135deg, #e05c5c, #a83030)', color: '#fff', fontWeight: '700', fontSize: '14px', borderRadius: '12px', cursor: 'pointer', marginBottom: '12px' },
  linkSkip: { fontSize: '13px', color: '#6b6860', cursor: 'pointer', textDecoration: 'underline' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '24px 20px 100px', position: 'relative', zIndex: 1 },
  scoreCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '24px', textAlign: 'center', marginBottom: '20px' },
  scoreLabel: { fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '8px' },
  scoreVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '72px', lineHeight: 1 },
  scoreSub: { fontSize: '13px', color: '#6b6860', marginBottom: '16px' },
  syncBtn: { display: 'inline-block', padding: '8px 20px', background: 'rgba(76,175,125,0.1)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '8px', color: '#4caf7d', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px' },
  lastSync: { fontSize: '11px', color: '#6b6860' },
  metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' },
  metricCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px' },
  metricTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' },
  metricIcon: { fontSize: '20px' },
  metricVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', lineHeight: 1 },
  metricUnit: { fontSize: '11px', fontFamily: "'DM Sans', sans-serif", color: '#6b6860' },
  metricLabel: { fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '8px' },
  metricBarBg: { height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden', marginBottom: '4px' },
  metricBarFill: { height: '100%', borderRadius: '2px', transition: 'width 0.8s ease' },
  metricGoal: { fontSize: '10px', color: '#555' },
  noteCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '14px 16px' },
  noteText: { fontSize: '12px', color: '#6b6860', lineHeight: '1.6' },
}
